/* eslint-disable @typescript-eslint/ban-ts-comment */
import type {
  CrossSectionChartContext,
  CrossSectionChartData,
  CrossSectionDataSet,
  CrossSectionTubingDataSet,
  EquipmentDimensionsContext,
  MLEDataContext,
  TopAndBottomCrossSectionChartData,
  TubingChartContext
} from "./d";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { getPumpChartDataSet } from "./getPumpChartDataSet";
import { getIntakeChartDataSet } from "./getIntakeChartDataSet";
import { getSealChartDataSet } from "./getSealChartDataSet";
import { getBoltOnDischargeChartDataSet } from "./getBoltOnDischargeChartDataSet";
import { getMotorChartDataSet } from "./getMotorChartDataSet";
import { getSensorChartDataSet } from "./getSensorChartDataSet";
import { getTubingChartDataSet } from "./getTubingChartDataSet";
import { Inches } from "@/libs/units/length";
import type { ConvertableUnit } from "@/libs/units";
import { getYToolChartDataSet } from "./getYToolChartDataSet";
import { calculateMleEllipseMaxDepth } from "@/libs/calculations/cables";
import { getCasingAtMD } from "@/libs/calculations/wellboreGeometry";

/**
 * Builds the cross-section chart context from equipment, well, and calculation data
 *
 * @param equipment Equipment configuration
 * @param wellProperties Well properties data
 * @param calcs Scenario calculations
 * @returns The cross-section chart context
 */
const buildCrossSectionChartContext = (
  calcs: ScenarioCalculations,
  lengthUnit: ConvertableUnit
): CrossSectionChartContext => {
  // @ts-expect-error
  const wellProperties = calcs.destructuredScenario.wellProperties?.value;
  // @ts-expect-error
  const equipment = calcs.destructuredScenario.equipment?.value;

  if (!wellProperties?.r_top_casing && !wellProperties?.r_bottom_casing) {
    return {};
  }

  const context: CrossSectionChartContext = {};

  const topCasing = wellProperties?.r_top_casing;
  const topCasingLengthFt = wellProperties?.top_casing_length_feet;
  const bottomCasing = wellProperties?.r_bottom_casing;
  const topTubing = wellProperties?.r_top_tubing;
  const topTubingLengthFt = wellProperties?.top_tubing_length_feet;
  const bottomTubing = wellProperties?.r_bottom_tubing;
  const bottomTubingLengthFt = wellProperties?.bottom_tubing_length_feet;
  const tubingChartContext: TubingChartContext[] = [];
  const bypassTubingODIn = calcs.yToolBypassTubingMaxODIn.value;
  const useYTool = !!equipment?.use_uphole_y_tool;
  const casingAt = (measuredDepthFt: number) =>
    getCasingAtMD(
      measuredDepthFt,
      wellProperties,
      calcs.equipmentLengths.totalCasingLengthFt.value
    );

  const cableCalcs = [];
  for (const calc of calcs.orderedCables.value) {
    const cableCalc = calcs.cableCalculations.individual.value[calc.id];
    if (!cableCalc.isSurface) {
      cableCalcs.push(cableCalc);
    }
  }

  // Y Tool data
  const yTool = equipment?.r_uphole_y_tool;
  const hasYTool = !!(yTool && useYTool);
  if (hasYTool) {
    const yToolCasing = casingAt(
      calcs.equipmentLengths.bottomOfYToolDepthFt.value
    );
    context.yToolDataContext = {
      // This will change to bypass tubing OD + max equip OD once that's found
      widthIn: yTool.width_inches,
      bypassTubingOuterDiameterIn: bypassTubingODIn || 0,
      casing: {
        innerDiameterIn: yToolCasing?.interior_diameter_inches,
        driftDiameterIn: yToolCasing?.drift_inches
      },
      lengthUnit
    };
  }

  // Find number of tubing charts we'll need with cable data
  if (topTubing) {
    let cableIdx = 1;
    for (const cableId in cableCalcs) {
      const c = cableCalcs[cableId];
      if (c!.downholeStartCableLengthFt < topTubingLengthFt) {
        tubingChartContext.push({
          tubingDataContext: {
            tubingCouplingODIn: topTubing.coupling_outer_diameter_inches,
            tubingLengthFt: topTubingLengthFt,
            tubingStartDepthFt: 0,
            tubingEndDepthFt: topTubingLengthFt,
            tubingOrientation: "Top"
          },
          cableDataContext: {
            widthIn: c!.xrefCable.r_cable?.width_inches,
            depthIn: c!.xrefCable.r_cable?.depth_inches,
            startDepthFt: c!.downholeStartCableLengthFt,
            endDepthFt: c!.downholeEndCableLengthFt,
            cableIndex: cableIdx
          },
          casingDataContext: null,
          lengthUnit
        });
      }
      cableIdx++;
    }
  }
  if (bottomTubing) {
    let cableIdx = 1;
    for (const cableId in cableCalcs) {
      const c = cableCalcs[cableId];
      if (
        c!.downholeStartCableLengthFt > topTubingLengthFt ||
        c!.downholeEndCableLengthFt > topTubingLengthFt
      ) {
        tubingChartContext.push({
          tubingDataContext: {
            tubingCouplingODIn: bottomTubing.coupling_outer_diameter_inches,
            tubingLengthFt: bottomTubingLengthFt,
            tubingStartDepthFt: topTubingLengthFt,
            tubingEndDepthFt: topTubingLengthFt + bottomTubingLengthFt,
            tubingOrientation: "Bottom"
          },
          cableDataContext: {
            widthIn: c!.xrefCable.r_cable?.width_inches,
            depthIn: c!.xrefCable.r_cable?.depth_inches,
            startDepthFt: c!.downholeStartCableLengthFt,
            endDepthFt: c!.downholeEndCableLengthFt,
            cableIndex: cableIdx
          },
          casingDataContext: null,
          lengthUnit
        });
      }
      cableIdx++;
    }
  }

  // Set casing data for each tubing chart
  for (const tubingChartSet of tubingChartContext) {
    // Check if the casing data context is already set
    if (!tubingChartSet.casingDataContext) {
      // Need to duplicate a charting set if both the tubing and cable
      // span both casings
      if (
        tubingChartSet.tubingDataContext.tubingStartDepthFt <
          topCasingLengthFt &&
        tubingChartSet.tubingDataContext.tubingEndDepthFt > topCasingLengthFt &&
        tubingChartSet.cableDataContext.startDepthFt < topCasingLengthFt &&
        tubingChartSet.cableDataContext.endDepthFt > topCasingLengthFt
      ) {
        tubingChartSet.casingDataContext = {
          innerDiameterIn: topCasing.interior_diameter_inches,
          driftDiameterIn: topCasing.drift_inches,
          casingOrientation: "Top"
        };
        const bottomCasingChartSet = { ...tubingChartSet };
        bottomCasingChartSet.casingDataContext = {
          innerDiameterIn: bottomCasing.interior_diameter_inches,
          driftDiameterIn: bottomCasing.drift_inches,
          casingOrientation: "Bottom"
        };
        tubingChartContext.push(bottomCasingChartSet);
      } // Tubing is contained in top casing
      else if (
        tubingChartSet.cableDataContext.endDepthFt <= topCasingLengthFt
      ) {
        tubingChartSet.casingDataContext = {
          innerDiameterIn: topCasing.interior_diameter_inches,
          driftDiameterIn: topCasing.drift_inches,
          casingOrientation: "Top"
        };
      }
      // Should be in bottom casing now
      else {
        tubingChartSet.casingDataContext = {
          innerDiameterIn: bottomCasing.interior_diameter_inches,
          driftDiameterIn: bottomCasing.drift_inches,
          casingOrientation: "Bottom"
        };
      }
    }
  }

  // Get pumps data
  const pumps = calcs.descendingOrderPumps.value.map(xrefPump => {
    const pump = xrefPump.r_pump;
    if (!pump) return null;
    return {
      id: xrefPump.id,
      outerDiameterIn: pump.housing_outer_diameter_inches,
      lengthIn: Inches.toThisUnit(xrefPump.length_feet),
      mleMaxEllipseDepthIn:
        calcs.pumpCalculations.individual.value[xrefPump.id]
          .mleMaxEllipseDepthIn,
      maxCalculatedOuterDiameterIn:
        calcs.pumpCalculations.individual.value[xrefPump.id]
          .maxCalculatedOuterDiameterIn
    };
  }) as EquipmentDimensionsContext[];
  const pumpCasing = casingAt(
    calcs.equipmentLengths.bottomOfPumpsDepthFt.value
  );

  // Get intakes data
  const intakes = calcs.orderedIntakes.value.map(xrefIntake => {
    const intake = xrefIntake.r_intake;
    if (!intake) return null;
    return {
      id: xrefIntake.id,
      outerDiameterIn: intake.outer_diameter_inches,
      lengthIn: intake.length_inches,
      mleMaxEllipseDepthIn:
        calcs.intakeCalculations.individual.value[xrefIntake.id]
          .mleMaxEllipseDepthIn,
      maxCalculatedOuterDiameterIn:
        calcs.intakeCalculations.individual.value[xrefIntake.id]
          .maxCalculatedOuterDiameterIn
    };
  }) as EquipmentDimensionsContext[];
  const intakeCasing = casingAt(
    calcs.equipmentLengths.bottomOfIntakesDepthFt.value
  );

  // Get seals data
  const seals = calcs.descendingOrderSeals.value.map(xrefSeal => {
    const seal = xrefSeal.r_seal;
    if (!seal) return null;
    return {
      id: xrefSeal.id,
      outerDiameterIn: seal.outer_diameter_inches,
      lengthIn: seal.length_inches,
      mleMaxEllipseDepthIn:
        calcs.sealCalculations.individual.value[xrefSeal.id]
          .mleMaxEllipseDepthIn,
      maxCalculatedOuterDiameterIn:
        calcs.sealCalculations.individual.value[xrefSeal.id]
          .maxCalculatedOuterDiameterIn
    };
  }) as EquipmentDimensionsContext[];
  const sealCasing = casingAt(
    calcs.equipmentLengths.bottomOfSealsDepthFt.value
  );

  // MLE data
  const mle = equipment?.r_mle;
  let mleData: MLEDataContext = {
    widthIn: 0,
    depthIn: 0
  };
  if (mle) {
    mleData = {
      widthIn: mle.width_inches,
      depthIn: mle.depth_inches
    };
  }

  // Find max OD out of all of the equipment
  let maxEquipmentODIn = 0;
  // Motor data
  const motor = equipment?.r_motor;
  if (motor) {
    const motorCasing = casingAt(
      calcs.equipmentLengths.bottomOfMotorDepthFt.value
    );

    context.motorDataContext = {
      outerDiameterIn: motor.outer_diameter_inches,
      maxCalculatedOuterDiameterIn: motor.outer_diameter_rub_inches,
      mleOffsetDepthIn: motor.mle_offset_depth_inches,
      casing: {
        innerDiameterIn: motorCasing?.interior_diameter_inches,
        driftDiameterIn: motorCasing?.drift_inches
      },
      hasYTool,
      mle: mleData,
      lengthUnit
    };
    // If there's a motor, set the maxEquipmentODIn to at least the motor OD
    maxEquipmentODIn = motor.outer_diameter_inches;
  }
  // Sensor data
  const sensor = equipment?.r_sensor;
  if (sensor) {
    const sensorCasing = casingAt(
      calcs.equipmentLengths.bottomOfEquipmentDepthFt.value
    );
    context.sensorDataContext = {
      outerDiameterIn: sensor?.outer_diameter_inches,
      motorOuterDiameterIn: 0,
      casing: {
        innerDiameterIn: sensorCasing?.interior_diameter_inches,
        driftDiameterIn: sensorCasing?.drift_inches
      },
      hasYTool,
      lengthUnit
    };
    if (motor) {
      context.sensorDataContext.motorOuterDiameterIn =
        motor.outer_diameter_inches;
    }
    if (sensor.outer_diameter_inches > maxEquipmentODIn) {
      maxEquipmentODIn = sensor.outer_diameter_inches;
    }
  }

  // Bolt-on discharge data
  const boltOnDischarge = equipment?.r_bolt_on_discharge;
  if (boltOnDischarge?.outer_diameter_inches) {
    const ellipseAngle = Math.atan(
      mleData.widthIn /
        2 /
        (boltOnDischarge.outer_diameter_inches + mleData.depthIn)
    );
    const mleMaxEllipseDepthIn = calculateMleEllipseMaxDepth(
      ellipseAngle,
      mleData.depthIn,
      boltOnDischarge.outer_diameter_inches,
      0,
      false
    );
    const boltOnDischargeCasing = casingAt(
      calcs.equipmentLengths.completePumpDepthFt.value
    );
    context.boltOnDischargeDataContext = {
      outerDiameterIn: boltOnDischarge.outer_diameter_inches,
      casing: {
        innerDiameterIn: boltOnDischargeCasing?.interior_diameter_inches,
        driftDiameterIn: boltOnDischargeCasing?.drift_inches
      },
      hasYTool,
      mle: mleData,
      maxCalculatedOuterDiameterIn:
        boltOnDischarge.outer_diameter_inches + mleMaxEllipseDepthIn,
      lengthUnit
    };
    if (boltOnDischarge.outer_diameter_inches > maxEquipmentODIn) {
      maxEquipmentODIn = boltOnDischarge.outer_diameter_inches;
    }
  }

  // Find max OD out of all of the equipment
  const allEquipment = [...pumps, ...intakes, ...seals];
  for (const equipmentItem of allEquipment) {
    if (equipmentItem.outerDiameterIn > maxEquipmentODIn) {
      maxEquipmentODIn = equipmentItem.outerDiameterIn;
    }
  }
  const effectiveYToolWidthIn = hasYTool
    ? maxEquipmentODIn + (bypassTubingODIn || 0)
    : 0;
  if (context.motorDataContext) {
    context.motorDataContext.maxEquipmentOuterDiameterIn = maxEquipmentODIn;
    context.motorDataContext.bypassTubingOuterDiameterIn =
      bypassTubingODIn || 0;
    context.motorDataContext.yToolWidthIn = effectiveYToolWidthIn;
  }
  if (context.sensorDataContext) {
    context.sensorDataContext.maxEquipmentOuterDiameterIn = maxEquipmentODIn;
    context.sensorDataContext.bypassTubingOuterDiameterIn =
      bypassTubingODIn || 0;
    context.sensorDataContext.yToolWidthIn = effectiveYToolWidthIn;
  }

  if (context.yToolDataContext) {
    context.yToolDataContext.maxEquipmentOuterDiameterIn = maxEquipmentODIn;
    context.yToolDataContext.widthIn = effectiveYToolWidthIn;
  }
  if (context.boltOnDischargeDataContext) {
    context.boltOnDischargeDataContext.maxEquipmentOuterDiameterIn =
      maxEquipmentODIn;
    context.boltOnDischargeDataContext.bypassTubingOuterDiameterIn =
      bypassTubingODIn || 0;
    context.boltOnDischargeDataContext.yToolWidthIn = effectiveYToolWidthIn;
  }

  // Construct the context object
  context.pumpDataContext = {
    pumps,
    casing: {
      innerDiameterIn: pumpCasing?.interior_diameter_inches,
      driftDiameterIn: pumpCasing?.drift_inches
    },
    mle: mleData,
    hasYTool,
    maxEquipmentOuterDiameterIn: maxEquipmentODIn,
    lengthUnit,
    ...(hasYTool && {
      bypassTubingOuterDiameterIn: bypassTubingODIn || 0,
      yToolWidthIn: effectiveYToolWidthIn
    })
  };
  context.intakeDataContext = {
    intakes,
    casing: {
      innerDiameterIn: intakeCasing?.interior_diameter_inches,
      driftDiameterIn: intakeCasing?.drift_inches
    },
    mle: mleData,
    hasYTool,
    maxEquipmentOuterDiameterIn: maxEquipmentODIn,
    lengthUnit,
    ...(hasYTool && {
      bypassTubingOuterDiameterIn: bypassTubingODIn || 0,
      yToolWidthIn: effectiveYToolWidthIn
    })
  };
  context.sealDataContext = {
    seals,
    casing: {
      innerDiameterIn: sealCasing?.interior_diameter_inches,
      driftDiameterIn: sealCasing?.drift_inches
    },
    mle: mleData,
    hasYTool,
    maxEquipmentOuterDiameterIn: maxEquipmentODIn,
    lengthUnit,
    ...(hasYTool && {
      bypassTubingOuterDiameterIn: bypassTubingODIn || 0,
      yToolWidthIn: effectiveYToolWidthIn
    })
  };
  context.tubingChartContext = tubingChartContext;

  return context;
};

const buildEquipmentDataSet = (
  context: CrossSectionChartContext
): CrossSectionChartData => {
  let pumpChartSet: CrossSectionDataSet[][] = [];
  // Get equipment datasets (pump, intake, seal, motor, sensor)

  let yToolChart: CrossSectionDataSet[] | null = null;
  if (context.yToolDataContext) {
    yToolChart = getYToolChartDataSet(context.yToolDataContext);
  }

  if (context.pumpDataContext && context.pumpDataContext.pumps.length > 0) {
    pumpChartSet = getPumpChartDataSet(context?.pumpDataContext);
  }

  let intakeChartSet: CrossSectionDataSet[][] = [];
  if (
    context.intakeDataContext &&
    context.intakeDataContext.intakes.length > 0
  ) {
    intakeChartSet = getIntakeChartDataSet(context.intakeDataContext);
  }

  let sealChartSet: CrossSectionDataSet[][] = [];
  if (context.sealDataContext && context.sealDataContext.seals.length > 0) {
    sealChartSet = getSealChartDataSet(context.sealDataContext);
  }

  let tubingChartSet: CrossSectionTubingDataSet[] = [];
  if (context.tubingChartContext && context.tubingChartContext?.length > 0) {
    tubingChartSet = getTubingChartDataSet(context.tubingChartContext);
  }
  let motorChart: CrossSectionDataSet[] | null = null;
  if (context.motorDataContext) {
    // Get motor dataset
    motorChart = getMotorChartDataSet(context.motorDataContext);
  }

  let sensorChart: CrossSectionDataSet[] | null = null;
  if (context.sensorDataContext && context.motorDataContext) {
    sensorChart = getSensorChartDataSet(context.sensorDataContext);
  }

  let boltOnDischargeChart: CrossSectionDataSet[] | null = null;
  if (context.boltOnDischargeDataContext) {
    boltOnDischargeChart = getBoltOnDischargeChartDataSet(
      context.boltOnDischargeDataContext
    );
  }

  return {
    pumpChartSet,
    intakeChartSet,
    sealChartSet,
    motorChart,
    sensorChart,
    tubingChartSet,
    yToolChart,
    boltOnDischargeChart
  };
};
/**
 * Hook to generate cross-section data for equipment
 *
 * @param equipment The equipment data
 * @param wellProperties The well properties data
 * @param scenarioCalculations The scenario calculations
 * @returns The complete cross-section chart data
 */
export const useCrossSectionChartingData = (
  scenarioCalculations: ScenarioCalculations,
  lengthUnit: ConvertableUnit,
  bottomScenarioCalculations?: ScenarioCalculations
): TopAndBottomCrossSectionChartData => {
  const topEquipmentContext = buildCrossSectionChartContext(
    scenarioCalculations,
    lengthUnit
  );
  const topEquipmentData = buildEquipmentDataSet(topEquipmentContext);

  let bottomEquipmentContext: CrossSectionChartContext | null = null;
  let bottomEquipmentData: CrossSectionChartData | null = null;
  // If there's bottom scenario calculations, build that context and datasets too
  if (bottomScenarioCalculations) {
    bottomEquipmentContext = buildCrossSectionChartContext(
      bottomScenarioCalculations,
      lengthUnit
    );
    bottomEquipmentData = buildEquipmentDataSet(bottomEquipmentContext);
  }

  return {
    topEquipmentData,
    bottomEquipmentData
  };
};
